import { API_URL } from 'shared/api/Constants';
import { apiInterceptor } from './axios';

// Mirrors ehr-sync-service's own PatientOnboardingStatus record - from
// getSelfSchedulingEligibilityAPI (patient-mgmt), passed straight through as query params on
// every self-scheduling free-slot read so the backend can apply its NEW-patient onboarding
// blackout without re-fetching this itself (see spec/2026-09-21-MM-1067-new-patient-onboarding-
// blackout.md). Only meaningful for a NEW patient - has no effect for an existing one either way.
export interface PatientOnboardingStatus {
  profileComplete: boolean;
  mfshComplete: boolean;
  mcatComplete: boolean;
  intakePaperworkComplete: boolean;
  migrationStatus?: string;
}

export interface SelfSchedulingProviderQuery {
  locationIds?: number[];
  consultationType?: string;
  // InsurancePay/CashPay - resolved once via getSelfSchedulingPatientContextAPI and sent
  // explicitly on every call from then on (see PatientSelfSchedulingContextDTO on the backend) -
  // required, not derived per-call any more.
  payMode: string;
  search?: string;
  // Lets the backend tell a brand new patient (no completed appointment yet - only "New
  // Patient" profiles bookable) apart from an existing one (every other profile instead).
  patientId?: number | string;
  // Narrows the result to one already-known provider (e.g. an existing patient's assigned
  // provider - see SelfScheduling/index.tsx's direct-entry redirect) instead of scanning and
  // computing a 90-day availability preview for every provider across every office, which is
  // both wasted work and, at real-data volume, prone to timing out.
  providerId?: number | string;
  onboardingStatus?: PatientOnboardingStatus;
}

const appendOnboardingStatusParams = (params: URLSearchParams, onboardingStatus?: PatientOnboardingStatus) => {
  if (!onboardingStatus) return;
  params.append('profileComplete', String(onboardingStatus.profileComplete));
  params.append('mfshComplete', String(onboardingStatus.mfshComplete));
  params.append('mcatComplete', String(onboardingStatus.mcatComplete));
  params.append('intakePaperworkComplete', String(onboardingStatus.intakePaperworkComplete));
  if (onboardingStatus.migrationStatus) {
    params.append('migrationStatus', onboardingStatus.migrationStatus);
  }
};

export const getSelfSchedulingLocationsAPI = () => {
  return apiInterceptor.get(`${API_URL.GET_SELF_SCHEDULING_LOCATIONS}`).then((response: any) => {
    return response;
  });
};

// Fetched once, up front, before any of the other self-scheduling calls: the patient's
// preselected provider (existing patients only), their payMode, and whether they can book at
// all right now (see PatientSelfSchedulingContextDTO on the backend - an active/upcoming
// appointment disables booking a second one).
export const getSelfSchedulingPatientContextAPI = (patientId: number | string) => {
  return apiInterceptor
    .get(`${API_URL.GET_SELF_SCHEDULING_PATIENT_CONTEXT}/${patientId}/context`)
    .then((response: any) => response);
};

export const getSelfSchedulingProvidersAPI = (query: SelfSchedulingProviderQuery) => {
  const params = new URLSearchParams();
  (query.locationIds ?? []).forEach((id) => params.append('locationIds', String(id)));
  if (query.consultationType) {
    params.append('consultationType', query.consultationType);
  }
  params.append('payMode', query.payMode);
  if (query.search) {
    params.append('search', query.search);
  }
  if (query.patientId) {
    params.append('patientId', String(query.patientId));
  }
  if (query.providerId) {
    params.append('providerId', String(query.providerId));
  }
  appendOnboardingStatusParams(params, query.onboardingStatus);
  const queryString = params.toString();
  const querySuffix = queryString ? `?${queryString}` : '';
  return apiInterceptor.get(`${API_URL.GET_SELF_SCHEDULING_PROVIDERS}${querySuffix}`).then((response: any) => {
    return response;
  });
};

export const getSelfSchedulingProviderAvailabilityAPI = (
  providerId: number | string,
  payMode: string,
  officeId?: number,
  weekStart?: string,
  patientId?: number | string,
  consultationType?: string,
  onboardingStatus?: PatientOnboardingStatus
) => {
  const params = new URLSearchParams();
  if (officeId) params.append('officeId', String(officeId));
  if (weekStart) params.append('weekStart', weekStart);
  if (patientId) params.append('patientId', String(patientId));
  if (consultationType) params.append('consultationType', consultationType);
  params.append('payMode', payMode);
  appendOnboardingStatusParams(params, onboardingStatus);
  const queryString = params.toString();
  const querySuffix = queryString ? `?${queryString}` : '';
  const url = `${API_URL.GET_SELF_SCHEDULING_PROVIDER_AVAILABILITY}/${providerId}/availability`;
  return apiInterceptor.get(`${url}${querySuffix}`).then((response: any) => {
    return response;
  });
};

export const bookSelfSchedulingAppointmentAPI = (data: any) => {
  return apiInterceptor.post(`${API_URL.BOOK_SELF_SCHEDULING_APPOINTMENT}`, data).then((response: any) => {
    return response;
  });
};

// Same /self-scheduling/appointments resource bookSelfSchedulingAppointmentAPI posts to -
// PUT/DELETE by chronoAppointmentId reschedule/cancel that same appointment (see
// ManageAppointment and ProviderAvailability's reschedule mode).
export const rescheduleSelfSchedulingAppointmentAPI = (appointmentId: number | string, data: any) => {
  return apiInterceptor
    .put(`${API_URL.BOOK_SELF_SCHEDULING_APPOINTMENT}/${appointmentId}`, data)
    .then((response: any) => response);
};

export const cancelSelfSchedulingAppointmentAPI = (appointmentId: number | string, data: any) => {
  return apiInterceptor
    .delete(`${API_URL.BOOK_SELF_SCHEDULING_APPOINTMENT}/${appointmentId}`, { data })
    .then((response: any) => response);
};

/**
 * MM-1191: staff / admin cancelling a patient's upcoming appointment from the patient chart (Appointment History).
 * Same request body as the patient's own cancellation, without the patient's 72-hour window; DrChrono is cancelled,
 * the free slot the appointment took is put back (if it took one) and the appointment shows as Cancelled.
 */
export const cancelAppointmentByStaffAPI = (appointmentId: number | string, data: any) => {
  return apiInterceptor
    .delete(`${API_URL.STAFF_CANCEL_APPOINTMENT}/${appointmentId}`, { data })
    .then((response: any) => response);
};

/**
 * MM-1168: free-slot rebuild state as stored on user_provider_location_mapping rows (empty = Pending);
 * COMPLETED_WITH_ERRORS is a location + provider roll-up only (rebuilt, but some mappings failed).
 * MM-1169: RETRY_LIMIT_REACHED - the rebuild and all its automatic retries failed.
 */
export type SlotRebuildStatus =
  | 'PENDING'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'COMPLETED_WITH_ERRORS'
  | 'FAILED'
  | 'RETRY_LIMIT_REACHED';

/**
 * MM-1169: what started a pair's latest rebuild (ehr-sync RebuildTrigger); a retry keeps it.
 * MM-1198: BREAK_UPDATE - a DrChrono break was added, moved or removed and its day was rebuilt.
 */
export type SlotRebuildTrigger = 'NIGHTLY' | 'TEMPLATE_SAVE' | 'SYNC_CALENDAR' | 'ADMIN_RESET' | 'BREAK_UPDATE';

/**
 * MM-1168: one office + provider pair on the Sync Calendar page with its rebuild tracking
 * (ehr-sync CalendarSyncPairDTO). Dates are epoch milliseconds; tracking fields are null when unknown.
 */
export interface CalendarSyncPair {
  officeId: number;
  officeName: string;
  providerId: number;
  providerName: string;
  profileCount: number;
  profileUpdatedDate: number | null;
  rebuildStatus: SlotRebuildStatus | null;
  rebuildStartedDate: number | null;
  lastCompletedDate: number | null;
  rebuildMessage: string | null;
  errorCount: number;
  mappings: CalendarSyncMapping[];
  /** MM-1169: what started the latest rebuild, null if unknown. */
  rebuildTrigger: SlotRebuildTrigger | null;
  /** MM-1169: automatic retries used by the latest rebuild. */
  retryCount: number;
  /** MM-1169: most automatic retries a failed rebuild gets. */
  retryLimit: number;
  /** MM-1169: when the next automatic retry is due (epoch ms), null if none. */
  nextRetryDate: number | null;
  /** MM-1169: when the pair was queued for a rebuild (epoch ms), null if never. */
  requestedDate: number | null;
  /** MM-1198: the pair's stored breaks since the 6:00 AM clean-up - each one triggers a full rebuild of the pair. */
  breaks: CalendarSyncBreak[];
}

/**
 * MM-1168: one profile mapping (appointment template) of a pair with its own rebuild status and
 * error (ehr-sync CalendarSyncMappingDTO). Dates are epoch milliseconds; weekDays 0 = Sunday.
 */
export interface CalendarSyncMapping {
  mappingId: number;
  profileId: number;
  profileName: string | null;
  slotDurationMinutes: number | null;
  startTime: string | null;
  endTime: string | null;
  windowMinutes: number | null;
  weekDays: number[];
  defaultExamRoom: string | null;
  secondaryExamRoom: string | null;
  profileUpdatedDate: number | null;
  rebuildStatus: SlotRebuildStatus | null;
  rebuildStartedDate: number | null;
  lastCompletedDate: number | null;
  rebuildMessage: string | null;
}

/** MM-1198: processing state of a stored DrChrono break (ehr-sync BreakEventStatus). */
export type BreakEventStatus =
  | 'PENDING'
  | 'INPROGRESS'
  | 'COMPLETED'
  | 'FAILURE'
  // MM-1198: not processed itself - a newer break of the same location + provider + exam room was rebuilt instead
  | 'SUPERSEDED'
  | 'IGNORED'
  | 'EXPIRED';

/**
 * MM-1198: one DrChrono break of a location + provider, with its processing status (ehr-sync
 * CalendarSyncBreakDTO). Dates are epoch milliseconds. Break rebuilds are not retried.
 */
export interface CalendarSyncBreak {
  eventId: number;
  appointmentId: string | null;
  eventType: 'APPOINTMENT_CREATE' | 'APPOINTMENT_MODIFY' | 'APPOINTMENT_DELETE';
  startDate: number | null;
  endDate: number | null;
  /** The break type: true for a recurring break, false for a single one. */
  recurring: boolean;
  status: BreakEventStatus;
  /** Why the rebuild failed, or why the break was ignored; null if none. */
  errorMessage: string | null;
  receivedDate: number | null;
  /** When its processing started, null if never picked up. */
  startedDate: number | null;
  completedDate: number | null;
}

/** MM-1168: what a Sync Calendar request did (ehr-sync CalendarSyncStatus). */
export type CalendarSyncStatus =
  | 'SYNCED'
  | 'SYNCED_WITH_ERRORS'
  | 'IN_PROGRESS'
  | 'ALREADY_RUNNING'
  | 'FAILED'
  // MM-1169: Sync queues the pair; the rebuild worker rebuilds one location + provider at a time
  | 'QUEUED';

/** MM-1168: answer to a Sync Calendar request; message is the text to show (ehr-sync CalendarSyncResponse). */
export interface CalendarSyncResponse {
  officeId: number;
  providerId: number;
  status: CalendarSyncStatus;
  message: string;
}

/**
 * MM-1168: every office + provider pair self-scheduling keeps free slots for, sorted by office then provider.
 * MM-1169: refreshInventory first re-reads DrChrono's offices and appointment profiles (page load / Refresh list).
 */
export const getCalendarSyncPairsAPI = (refreshInventory = false) => {
  return apiInterceptor
    .get(`${API_URL.GET_CALENDAR_SYNC_PAIRS}`, { params: { refreshInventory } })
    .then((response: any) => response);
};

/**
 * MM-1168: re-reads one pair's DrChrono calendar (appointments and breaks) and rebuilds its
 * self-scheduling free slots. MM-1169: only queues the pair - resolves on 202 QUEUED (the rebuild worker
 * rebuilds it, one location + provider at a time; follow its status in the pair list); rejects on 409
 * ALREADY_RUNNING / 500 FAILED with the CalendarSyncResponse in the rejected response's data
 * (apiInterceptor rejects with the AxiosResponse itself).
 */
export const syncCalendarAPI = (officeId: number, providerId: number) => {
  return apiInterceptor
    .post(`${API_URL.SYNC_CALENDAR}`, null, { params: { officeId, providerId } })
    .then((response: any) => response);
};
